import React, { useMemo } from "react";
import { NavLink } from "reactstrap";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";

import { useSidebarContext } from "../../components/context/SidebarContext";
import AppRoutes from "../../AppRoutes";
import useAuthStore from "../stores/useAuthStore";

const Sidebar = () => {
  const logout = useAuthStore((state) => state.logout);

  const location = useLocation();
  const { open, setOpen } = useSidebarContext();
  const path = useMemo(() => `/${location.pathname.split("/")[1]}`, [location]);

  const minimizeSidebar = () => setOpen(!open);

  const isSupportPage = path === "/support";

  return (
    <StyleWrapper
      className={`sidebar-container ${!open && "sidebar-collapsed"}`}
    >
      <div className="sidebar rounded-box d-flex flex-column">
        <div className="sidebar-section d-flex justify-content-between align-items-center">
          <NavLink tag={Link} to="/" className="logo-link">
            <img
              className="logo"
              src={
                !open
                  ? "/assets/icons/sidebar-logo-collapsed.svg"
                  : "/assets/icons/smart-doc.svg"
              }
              alt=""
            />
          </NavLink>
          <button
            className="sidebar-collapse-btn d-flex align-items-center justify-content-center"
            onClick={minimizeSidebar}
          >
            <img
              src={`/assets/icons/chevron-${!open ? "right" : "left"}.svg`}
              alt=""
            />
          </button>
        </div>

        <div className="sidebar-section menu-link-section">
          {AppRoutes.map((item, index) => {
            const isActive = item.path === path;
            const imageName = `${item.icon}${isActive ? "-active" : ""}.svg`;

            return (
              <NavLink
                tag={Link}
                to={item.path}
                className={`menu-link ${isActive ? "active" : ""}`}
                key={index}
              >
                <span
                  className={` ${
                    open && "d-flex justify-content-between align-items-center"
                  }`}
                >
                  <span className="d-flex align-items-center">
                    <img
                      className="menu-icon"
                      src={`/assets/icons/${imageName}`}
                      alt={`menu-icon` + item.name}
                      title={item.name}
                    />
                    <span className={`description ${!open && "d-none"}`}>
                      {item.name}
                    </span>
                  </span>
                  <img
                    className={!open ? "d-none" : ""}
                    src="/assets/icons/chevron-right.svg"
                    alt=""
                  />
                </span>
              </NavLink>
            );
          })}
        </div>

        <div className="sidebar-section mt-auto">
          <NavLink
            tag={Link}
            to="/support"
            className={`menu-link ${isSupportPage ? "active" : ""}`}
          >
            <span
              className={
                open && "d-flex justify-content-between align-items-center"
              }
            >
              <span className="d-flex align-items-center">
                <img
                  className="menu-icon"
                  src={`/assets/icons/support-assistance${
                    isSupportPage ? "-active" : ""
                  }.svg`}
                  alt=""
                />
                <span className={`description ${!open && "d-none"}`}>
                  Support et Assistance
                </span>
              </span>
            </span>
          </NavLink>
          <button className="menu-link nav-link logout" onClick={logout}>
            <span className={` ${open && "d-flex align-items-center"}`}>
              <img
                className="menu-icon"
                src="/assets/icons/logout.svg"
                alt="logout-icon"
              />
              <span className={`description ${!open && "d-none"}`}>
                Déconnexion
              </span>
            </span>
          </button>
        </div>

        <div className="sidebar-section profile-section ">
          <div
            className={`profile-container ${
              open && "d-flex justify-content-between align-items-center"
            }`}
          >
            <span className="d-flex align-items-center">
              <span className="profile-icon-container d-flex align-items-center justify-content-center">
                <img
                  className="profile-icon"
                  src="/assets/icons/client-logo.svg"
                  alt=""
                />
              </span>
              <span className={`profile-info ${!open && "d-none"}`}>
                <p>Catherine De Jaegere</p>
                <p>Latour & Petit</p>
              </span>
            </span>
            {open && (
              <button className="profile-more-btn">
                <img src="/assets/icons/ellipse.svg" alt="" />
              </button>
            )}
          </div>
        </div>
      </div>
    </StyleWrapper>
  );
};

const StyleWrapper = styled.div`
  &.sidebar-container {
    position: relative;
    height: 100vh;
    width: 384px;
    transition: width 0.3s;
    padding: 20px 10px;

    .logo {
      width: 196px;
      height: 63px;
    }

    &.sidebar-collapsed {
      width: fit-content;

      .sidebar-collapse-btn {
        position: absolute;
        right: 0;
        left: 60px;
      }

      .nav-link {
        padding: 12px;

        &.logo-link {
          padding: 0;
        }
      }

      .logo {
        width: 40px;
        height: 40px;
      }
    }

    .sidebar-collapse-btn {
      border: none;
      width: 20px;
      height: 20px;
      border-radius: 6px;
    }

    .sidebar {
      position: relative;
      height: 100%;
    }

    .logo-link {
      padding: 0 15px;
      border-radius: 0;
    }

    .sidebar-section {
      padding: 10px 15px;
      border-bottom: 1px solid var(--border-color);
    }

    .menu-link-section {
      flex-grow: 1;
    }

    .btn-collapse {
      position: absolute;
      right: -15px;
      border: 0;
      top: calc(100% / 2.2);
      background: var(--blue);
      box-shadow: 0px 2px 5px 0px #26334d08;
      border-top-right-radius: 5px;
      border-bottom-right-radius: 5px;
      padding: 0;
      height: 50px;
      width: 15px;

      img {
        vertical-align: bottom;
        margin-left: -4px;
      }
    }

    .menu-link {
      color: var(--text-color);
      text-decoration: none;
      font-weight: 500;

      &.active {
        background-color: var(--menu-active-color);
        font-weight: 800;

        img.menu-icon {
          background-color: var(--red-color);
        }
      }

      img.menu-icon {
        background-color: var(--menu-active-color);
        vertical-align: bottom;
        padding: 7px;
        border-radius: 6px;
      }

      .description {
        margin-left: 18px;
      }
    }

    .nav-link {
      border-radius: 10px;
      padding: 12px 20px;
    }

    .profile-section {
      border-bottom: none;
    }

    .profile-container {
      padding: 12px 20px;
    }

    .profile-icon-container {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      overflow: hidden;
      box-shadow: 0px 0px 6px 0px #0000000d;
    }

    &.sidebar-collapsed .profile-container {
      padding: 0;
      display: flex;
      justify-content: center;
    }

    .profile-info {
      margin-left: 10px;
    }

    .profile-info p:nth-child(1) {
      font-weight: 800;
      margin: 0;
    }

    .profile-info p:nth-child(2) {
      font-size: 12px;
      margin: 0;
    }

    .profile-more-btn {
      border: none;
      width: 40px;
      height: 40px;
      background: none;
    }

    .logout {
      color: var(--red-color) !important;
    }
  }
`;

export default Sidebar;
